import type { SentimentThemeMode } from './constants'

/** 情绪页黑版页面底色 */
export const SENTIMENT_DARK_BG = 'rgba(7, 7, 7, 1)'

export interface SentimentThemeTokens {
  bg: string
  panelBg: string
  border: string
  text: string
  textSecondary: string
  chartGrid: string
  chartAxis: string
  scoreUp: string
  scoreDown: string
  scoreFlat: string
  tableHeader: string
  tableRowActive: string
  toggleBorder: string
  toggleActive: string
}

const dark: SentimentThemeTokens = {
  bg: SENTIMENT_DARK_BG,
  panelBg: '#1a1a1a',
  border: '#2a2a2a',
  text: '#e4e4e4',
  textSecondary: '#8c8c8c',
  chartGrid: '#2a2a2a',
  chartAxis: '#666',
  scoreUp: '#fe4043',
  scoreDown: '#39e365',
  scoreFlat: '#8c8c8c',
  tableHeader: '#8c8c8c',
  tableRowActive: 'rgba(57, 227, 101, 0.12)',
  toggleBorder: '#444',
  toggleActive: '#e4e4e4'
}

const light: SentimentThemeTokens = {
  bg: '#f2f5fa',
  panelBg: '#ffffff',
  border: '#e8e8e8',
  text: '#333333',
  textSecondary: '#7d7d7d',
  chartGrid: '#e8e8e8',
  chartAxis: '#999',
  scoreUp: '#cc2b1d',
  scoreDown: '#439731',
  scoreFlat: '#7d7d7d',
  tableHeader: '#7d7d7d',
  tableRowActive: 'rgba(67, 151, 49, 0.1)',
  toggleBorder: '#d9d9d9',
  toggleActive: '#333'
}

export function getSentimentTheme(mode: SentimentThemeMode): SentimentThemeTokens {
  return mode === 'light' ? light : dark
}

/**
 * 情绪五档 RGB（tier 1=0–19 … tier 5=≥80，与 SENTIMENT_STAGES 一致）
 * 蓝 / 绿 / 金黄 / 橙红 / 红
 */
const STAGE_TIER_RGB: Record<SentimentThemeMode, Record<number, [number, number, number]>> = {
  dark: {
    1: [79, 163, 255],
    2: [57, 227, 101],
    3: [232, 179, 57],
    4: [255, 101, 5],
    5: [254, 64, 67]
  },
  light: {
    1: [79, 163, 255],
    2: [67, 151, 49],
    3: [232, 179, 57],
    4: [255, 101, 5],
    5: [254, 64, 67]
  }
}

function stageTierRgba(
  tier: number,
  theme: SentimentThemeMode,
  alpha: number
): string {
  const rgb = STAGE_TIER_RGB[theme][tier] || STAGE_TIER_RGB[theme][3]
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
}

/** 情绪五档文案色（与 StageTable tier / signal 一致） */
export const STAGE_TIER_TEXT_COLOR: Record<number, string> = {
  1: '#4fa3ff',
  2: '#39e365',
  3: '#e8b339',
  4: '#fa8e00',
  5: '#fe4043'
}

export function getStageTierTextColor(tier: number): string {
  const t = Math.max(1, Math.min(5, Math.round(tier)))
  return STAGE_TIER_TEXT_COLOR[t] || STAGE_TIER_TEXT_COLOR[3]
}

/** 情绪解读表格选中行背景（0–19 蓝 … ≥80 红） */
export function getStageRowActiveBg(
  tier: number,
  theme: SentimentThemeMode = 'dark'
): string {
  const alphaByTier: Record<number, number> =
    theme === 'light'
      ? { 5: 0.08, 4: 0.08, 3: 0.08, 2: 0.1, 1: 0.1 }
      : { 5: 0.15, 4: 0.12, 3: 0.12, 2: 0.12, 1: 0.15 }
  return stageTierRgba(tier, theme, alphaByTier[tier] ?? 0.12)
}

/** 情绪趋势图 5 档背景色（自上而下：≥80 红 … 0–19 蓝） */
export function getScoreBandColors(theme: SentimentThemeMode): string[] {
  const alpha =
    theme === 'light'
      ? { 5: 0.08, 4: 0.08, 3: 0.08, 2: 0.08, 1: 0.12 }
      : { 5: 0.15, 4: 0.12, 3: 0.12, 2: 0.08, 1: 0.15 }
  return [5, 4, 3, 2, 1].map((tier) => stageTierRgba(tier, theme, alpha[tier]))
}
